import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { ApiService } from '@services/api.service';
import { UiHelper } from '@helpers/ui.helper';
import { NavigationEnd, Router } from '@angular/router';
import {
  CasinoCategoriesListModel,
  CasinoGameListModel,
  CasinoGameModel,
  casinoGamesItemType,
  casinoGamesListApiType,
  CasinoProviderListModel,
  DebouncedFunction,
  DebouncedInputFunction,
  DebouncedProviderFunction,
  goGamesApiType,
} from '@components/casino/casino.model';
import { filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { debounce } from 'lodash';
import { FormControl } from '@angular/forms';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { CASINO_CONFIG } from '@components/casino/casino.config';
import { CasinoHelper } from '@components/casino/casino.helper';
import { LocalizationService } from '@services/localization.service';
import { TranslateService } from '@services/translate.service';
import { CasinoLoadingService } from '@components/casino/casino-loading.service';

@Component({
  selector: 'casino-lobby-component',
  styleUrls: ['./casino.sass'],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './casino-lobby.component.html',
  animations: [
    trigger('openCloseProviderMenuOne', [
      state(
        'open',
        style({
          opacity: 1,
        })
      ),
      state(
        'closed',
        style({
          backgroundColor: 'var(--casino-slider-bg)',
          height: '50px',
          flexWrap: 'no-wrap',
          overflowY: 'hidden',
          overflowX: 'scroll',
        })
      ),
      transition('open <=> closed', [animate('.3s')]),
    ]),
    trigger('openCloseProviderMenuTwo', [
      state(
        'open',
        style({
          opacity: 1,
        })
      ),
      state(
        'closedOneColumn',
        style({
          backgroundColor: 'var(--casino-slider-bg)',
          height: '50px',
          flexWrap: 'no-wrap',
          overflowY: 'hidden',
          overflowX: 'scroll',
        })
      ),
      state(
        'closed',
        style({
          backgroundColor: 'var(--casino-slider-bg)',
          height: '0',
          opacity: 0.8,
          flexWrap: 'no-wrap',
          overflowY: 'hidden',
          overflowX: 'scroll',
        })
      ),
      transition('open <=> closed', [animate('.3s')]),
    ]),
  ],
})
export class CasinoLobbyComponent implements AfterViewInit, OnInit, OnDestroy {
  public casinoCategory: number | string = CASINO_CONFIG.startingCategoryId;
  public casinoProvider: number | string = CASINO_CONFIG.startingProviderId;
  public page: number = CASINO_CONFIG.gameStartingPage;
  public limit: number = CASINO_CONFIG.gameLimit;
  public isSearchButton = false;
  public isOpenProviderMenu: boolean = CASINO_CONFIG.startingProviderMenuState;
  public isGameListLoading = true;
  public isFavGameListLoading = true;

  public casinoCategoriesList: CasinoCategoriesListModel = new CasinoCategoriesListModel();
  public casinoProviderList: CasinoProviderListModel = new CasinoProviderListModel();
  public gameDataList: CasinoGameListModel = new CasinoGameListModel({});
  public favGameDataList: CasinoGameListModel = new CasinoGameListModel({});
  public mostFavoritesGameList: CasinoGameListModel = new CasinoGameListModel({});

  private readonly debouncedOnScroll: DebouncedFunction;
  private debouncedInput: DebouncedInputFunction;
  private selectCasinoProviderAction: DebouncedProviderFunction;
  private selectCasinoCategoryAction: DebouncedProviderFunction;
  public searchControl: FormControl<string> = new FormControl();
  public searchPrevValue: string | null;
  private searchSubscription: Subscription;

  private subscriptions: Subscription[] = [];

  public isFavPage = false;
  public isOpenGameModal = false;
  public openGameData: CasinoGameModel;
  public openGameDataApi: goGamesApiType;

  private oldCurrentTheme = '';

  constructor(
    private _ApiService: ApiService,
    public _UiHelper: UiHelper,
    private _router: Router,
    public _MatDialog: MatDialog,
    private renderer: Renderer2,
    public _CasinoService: CasinoHelper,
    public _LocalizationService: LocalizationService,
    public _LoadingService: CasinoLoadingService,
    private _TranslateService: TranslateService
  ) {
    this._CasinoService.getSliderApiCall();
    this.routeControlAction(this._router.url);
    this.debouncedOnScroll = debounce(this.onScroll, 200) as DebouncedFunction;
    this.subscriptions.push(
      this._CasinoService.favChangeListener.subscribe(() => {
        this.favListApiCall();
      }),
      this._CasinoService.selectProviderListener.subscribe((x: number) => {
        this.selectCasinoProviderAction(x);
      }),
      this._CasinoService.selectCategoryListener.subscribe((x: number) => {
        this.selectCasinoCategoryAction(x);
      }),
      this._CasinoService.goGamesListener.subscribe((x: CasinoGameModel) => {
        this.goGames(x);
      })
    );
  }

  @ViewChild('headerSearch') headerSearch!: ElementRef;
  @ViewChild('providerMain') providerMain!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;

  @HostListener('window:scroll', [])
  @HostListener('window:touchmove', [])
  handleScroll() {
    this.debouncedOnScroll();
  }

  onScroll(): void {
    if (this.bottomReached() && !this.isGameListLoading && !this.gameDataList.isDataAllowed) {
      this.page++;
      this.setCasinoGamesList({
        page: this.page,
        limit: this.limit,
        providerId: this.casinoProvider,
        categoryId: this.casinoCategory,
        searchValue: this.searchControl.value,
      });
    }
  }

  trackByProviderId(index: number, provider: any): number {
    return provider.providerId;
  }

  bottomReached(): boolean {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
    const footerHeight = 360;

    return scrollTop + window.innerHeight >= scrollHeight - footerHeight - 400;
  }

  ngOnInit() {
    // this.oldCurrentTheme = localStorage.getItem('themeState');
    // this._UiHelper.changeTheme('theme-dark');
    this.casinoCategoryApi();
    this.casinoProviderApi();
    this.favListApiCall();
    this.mostFavoritesApiCall();
    this.debouncedInput = debounce(value => {
      if (typeof value === 'string' && value.length >= 3) {
        this.closeFav();
        this.page = CASINO_CONFIG.gameStartingPage;
        this.casinoProvider = CASINO_CONFIG.startingProviderId;
        this.casinoCategory = CASINO_CONFIG.startingCategoryId;
        this.setCasinoGamesList({
          page: this.page,
          limit: this.limit,
          providerId: this.casinoProvider,
          categoryId: this.casinoCategory,
          searchValue: value,
        });
      } else if ((this.searchPrevValue && this.searchPrevValue.length > 0 && (value === null || value === '')) || value === null) {
        this.setCasinoGamesList({
          page: this.page,
          limit: this.limit,
          providerId: this.casinoProvider,
          categoryId: this.casinoCategory,
        });
      }

      this.searchPrevValue = value;
    }, CASINO_CONFIG.searchInputDebounceMillisecond);

    this.searchSubscription = this.searchControl.valueChanges.subscribe(value => {
      this.debouncedInput(value);
    });

    this.selectCasinoProviderAction = debounce((provider: number) => {
      if (provider !== this.casinoProvider) {
        this.closeFav();
        this._router.navigate([`/casino/lobby/${this.casinoCategory}/${provider}`]).then();
        this.casinoProvider = provider;
        this.searchInputReset();
        if (window.innerWidth <= CASINO_CONFIG.mobileVersionWidth) {
          this.isOpenProviderMenu = false;
          this._UiHelper.ScrollToTop();
        }
      }
    }, 500);
    this.selectCasinoCategoryAction = debounce((category: number) => {
      this.closeFav();
      this._router.navigate([`/casino/lobby/${category}/${CASINO_CONFIG.startingProviderId}`]).then();
      this.casinoCategory = category;
      this.casinoProvider = CASINO_CONFIG.startingProviderId;
      this.casinoProviderApi();
      this.searchInputReset();
    }, 500);
  }

  mostFavoritesApiCall() {
    this._ApiService
      .getCasinoMostFavorites({
        count: 16,
      })
      .then(x => {
        this.mostFavoritesGameList = new CasinoGameListModel({
          response: x,
          dataType: 'mostFavorites',
        });
      });
  }

  favListApiCall(): void {
    this._ApiService
      .getCasinoFavGameList({
        page: CASINO_CONFIG.favGameStartingPage,
        limit: CASINO_CONFIG.favGameLimit,
      })
      .then((x: casinoGamesItemType[]) => {
        this.favGameDataList = new CasinoGameListModel({
          response: x,
          dataType: 'fav',
        });
        this._CasinoService.setFavList(this.favGameDataList.favList);
      })
      .finally(() => {
        this.isFavGameListLoading = false;
      });
  }

  ngAfterViewInit() {
    this.subscriptions.push(
      this._router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(({ urlAfterRedirects }: NavigationEnd) => {
        this.routeControlAction(urlAfterRedirects);
      })
    );

    if (this._UiHelper.leftMenu) this._UiHelper.setLeftMenu();
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.selectCasinoProviderAction.cancel();
    this.debouncedOnScroll.cancel();
    this.debouncedInput.cancel();
    for (const subscription of this.subscriptions) {
      if (subscription) {
        subscription.unsubscribe();
      }
    }
    // this._UiHelper.changeTheme(this.oldCurrentTheme);
  }

  closeFav() {
    if (this.isFavPage) {
      this.favIsOpenAction();
    }
  }

  favIsOpenAction(): void {
    this.isFavPage = !this.isFavPage;
  }

  providerMenuOpenControl() {
    this.isOpenProviderMenu = !this.isOpenProviderMenu;
  }

  searchButtonClick() {
    if (window.innerWidth < CASINO_CONFIG.mobileVersionWidth) {
      this.closeFav();
      this.isSearchButton = true;
      const headerSearchElement = this.headerSearch.nativeElement;
      this.renderer.setStyle(headerSearchElement, 'left', '0');
      this.casinoCategory = CASINO_CONFIG.startingCategoryId;
      this.casinoProvider = CASINO_CONFIG.startingProviderId;

      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      }, 700); // animasyon süresi dikkate alındı
    }
  }

  closedSearchButtonClick() {
    if (window.innerWidth < CASINO_CONFIG.mobileVersionWidth) {
      this.isSearchButton = false;
      const headerSearchElement = this.headerSearch.nativeElement;
      this.renderer.setStyle(headerSearchElement, 'left', 'calc((100vw - 2em) - 35px)');
    }
    this.searchInputReset();
  }

  casinoCategoryApi() {
    this._ApiService.getCasinoCategoriesList().then(x => {
      this.casinoCategoriesList = new CasinoCategoriesListModel(x);
      this.searchInputReset();
    });
  }

  searchInputReset(): void {
    if (this.searchControl.value) {
      this.searchControl.reset();
    }
  }

  casinoProviderApi() {
    this._ApiService.getCasinoProviderList(this.casinoCategory).then(x => {
      this.casinoProviderList = new CasinoProviderListModel(x, {
        ...CASINO_CONFIG.providerAll,
        ProviderName: this._TranslateService.instant(`casino.lobby.${CASINO_CONFIG.providerAll.ProviderName}`),
      });
    });
  }

  routeControlAction(url: string): void {
    this.gameDataList.isDataAllowed = false;
    this.page = CASINO_CONFIG.gameStartingPage;
    const urlList: string[] = url.split('/');
    if (urlList.length === 5) {
      this.casinoCategory = Number(urlList[3]);
      this.casinoProvider = Number(urlList[4]);
      this.setCasinoGamesList({
        page: this.page,
        limit: this.limit,
        providerId: this.casinoProvider,
        categoryId: this.casinoCategory,
        searchValue: this.searchControl.value,
      });
    }
  }

  setCasinoGamesList({ page, limit, providerId, categoryId, searchValue }: casinoGamesListApiType): void {
    this.isGameListLoading = true;
    this._ApiService
      .getCasinoGameList({
        page,
        limit,
        providerId,
        categoryId,
        searchValue,
      })
      .then((x: casinoGamesItemType[]) => {
        this.gameDataList = new CasinoGameListModel({
          response: x,
          prevGameList: this.gameDataList,
          page: this.page,
        });
      })
      .finally(() => {
        this.isGameListLoading = false;
      });
  }

  closedGameModal() {
    this.isOpenGameModal = false;
  }

  goGames(item: CasinoGameModel) {
    this.goSoftmayaCasino(item);
  }

  goSoftmayaCasino(item: CasinoGameModel) {
    this._LoadingService.open();
    this._ApiService
      .getCasinoSoftmayaUrl(item.id, window.innerWidth > 990 ? 'desktop' : 'mobile')
      .then(x => {
        this.openGameDataApi = x;
        this.openGameData = item;
      })
      .catch(() => {
        this._CasinoService.snackbarWarningMessage('error', 'Bir Hata oluştu. Bir süre sonra tekrar deneyiniz');
      })
      .finally(() => {
        this.isOpenGameModal = true;
        this._LoadingService.close();
      });
  }

  protected readonly CASINO_CONFIG = CASINO_CONFIG;
}
